import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import { EXPIRATION_OPTIONS } from "../data/types";

// Scope selector was removed because it confused users and there was no real use case
// for users changing it.
//
// To restore, if needed in the future:
//   1. Add `scope`, `scopeResourceType`, `defaultScope`, and their `on*Change`
//      props back to this component.
//   2. Render a <ResourcePicker> section (see git history of this file).
//   3. Re-wire those props from NewAccessRequestForm.tsx.
//
// Scope is still in form state and auto-derived via getDefaultScope, so the API
// submission keeps working.

const OptionSection = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
  "&:last-child": {
    paddingBottom: 0,
  },
}));

const CheckboxGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

type AdditionalOptionsFieldProps = {
  shareable: boolean;
  expirationDays: number | null;
  onShareableChange: (value: boolean) => void;
  onExpirationDaysChange: (value: number | null) => void;
};

function AdditionalOptionsField({
  shareable,
  expirationDays,
  onShareableChange,
  onExpirationDaysChange,
}: AdditionalOptionsFieldProps) {
  const handleExpirationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    onExpirationDaysChange(value === "permanent" ? null : Number(value));
  };

  return (
    <Box>
      <OptionSection>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Expiration
        </Typography>
        <TextField
          select
          label="Duration"
          value={expirationDays ?? "permanent"}
          onChange={handleExpirationChange}
          fullWidth
        >
          {EXPIRATION_OPTIONS.map((option) => (
            <MenuItem
              key={option.value ?? "permanent"}
              value={option.value ?? "permanent"}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </OptionSection>

      <OptionSection>
        <Typography variant="subtitle2" gutterBottom>
          Permission Options
        </Typography>
        <CheckboxGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={shareable}
                onChange={(e) => onShareableChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Shareable</Typography>
                <Typography variant="caption" color="text.secondary">
                  Check this box if you would like to be able to share this
                  access further
                </Typography>
              </Box>
            }
          />
        </CheckboxGroup>
      </OptionSection>
    </Box>
  );
}

export default AdditionalOptionsField;
